import React, { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { CareerIcon } from 'atoms';
import { ICareerGoal } from 'core';
import { progressSources } from 'core/hooks/Growth/transform/transformSkillProgress';
import { skillMapHref } from 'router/careerLinks';
import { PATHS } from 'router/config';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';
import { mobile } from 'services/ui';

import { PlanCard, PlanCta, PlanLabel, PlanText, PlanTitle } from './CareerPlanAction';

interface ICareerPlanGoal {
  goal: ICareerGoal | null;
  hasGoal: boolean;
}

/**
 * Цель на главной (ТЗ 0071, п. 4.2 и 4.3; «визуальное упрощение», 5.3).
 *
 * Цели нет — одно предложение выбрать и НИ ОДНОГО персонального числа: сервер
 * подставляет первую профессию «по умолчанию», и «10 z 22» по ней было бы
 * дистанцией до работы, которую человек не выбирал. Это и есть основное
 * действие, поэтому оно в той же белой карточке с жёлтой кнопкой.
 *
 * Цель есть — спокойный блок без карточки: профессия, навыки одной фразой,
 * «Otwórz mapę» и «Zmień cel». Фаза больше не пишется здесь — её показывает
 * маршрут меткой «Teraz». Подтверждённые школой — в раскрытии «Jak liczymy
 * postęp?»: самостоятельно указанный навык подтверждённым не называется, но и
 * второй строкой под главным числом эта оговорка не стоит.
 */
export const CareerPlanGoal = memo(({ goal, hasGoal }: ICareerPlanGoal) => {
  const { t } = useTranslation();
  const [explained, setExplained] = useState(false);

  if (!hasGoal || !goal) {
    return (
      <PlanCard aria-labelledby="career-plan-pick-title">
        <PlanTitle id="career-plan-pick-title">
          <CareerIcon name="goal" size={24} />
          <span>{t('careerCenter.plan.pick.title')}</span>
        </PlanTitle>
        <PlanText>{t('careerCenter.plan.pick.lead')}</PlanText>
        <PlanCta to={PATHS.careerProfessions}>{t('careerCenter.plan.pick.cta')}</PlanCta>
        <PlanLabel>{t('careerCenter.plan.pick.saved')}</PlanLabel>
      </PlanCard>
    );
  }

  const progress = goal.skillProgress;

  return (
    <Goal aria-labelledby="career-plan-goal-title">
      <Body>
        <Label>{t('careerCenter.plan.goal.label')}</Label>
        <Title id="career-plan-goal-title">{goal.title}</Title>
        {progress && progress.requiredCount > 0 ? (
          <>
            {/* 0072: одна фраза на главной, странице профессии и карте. */}
            <Skills>
              {t('careerCenter.skillProgress.meets', {
                meets: progress.meetsCount,
                total: progress.requiredCount,
              })}
            </Skills>
            <HowButton
              type="button"
              aria-expanded={explained}
              aria-controls="career-plan-goal-how"
              onClick={() => setExplained((value) => !value)}>
              {t('careerCenter.plan.goal.howTitle')}
              <Chevron $open={explained} aria-hidden>
                <CareerIcon name="chevronDown" size={16} />
              </Chevron>
            </HowButton>
            <How id="career-plan-goal-how" hidden={!explained}>
              <p>
                {t('careerCenter.skillProgress.sources', {
                  verified: progressSources(progress).verified,
                  self: progressSources(progress).selfAssessed,
                })}
              </p>
              <p>{t('careerCenter.plan.goal.howText')}</p>
            </How>
          </>
        ) : (
          <Skills>{t('careerCenter.plan.goal.noRequirements')}</Skills>
        )}
      </Body>
      <Links>
        <MapLink to={skillMapHref({ track: goal.trackCode })}>
          {t('careerCenter.plan.goal.openMap')}
        </MapLink>
        <ChangeLink to={PATHS.careerProfessions}>{t('careerCenter.plan.goal.change')}</ChangeLink>
      </Links>
    </Goal>
  );
});

const Goal = styled.section`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 32px;

  ${mobile} {
    flex-direction: column;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Label = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const Title = styled.h2`
  margin: 4px 0 0;
  color: ${careerColor.text};
  overflow-wrap: anywhere;
  ${careerType.professionTitle()};
`;

const Skills = styled.p`
  margin: 8px 0 0;
  color: ${careerColor.text};
  ${careerType.body()};
`;

const HowButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${careerColor.text2};
  cursor: pointer;
  ${careerType.meta()};

  &:hover {
    color: ${careerColor.text};
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const How = styled.div`
  max-width: 70ch;
  color: ${careerColor.text2};
  ${careerType.meta()};

  p {
    margin: 0 0 8px;
  }
`;

const Links = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const MapLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.hitTarget}px;
  color: ${careerColor.text};
  text-decoration: underline;
  text-underline-offset: 3px;
  ${careerType.body()};

  &:hover {
    color: ${careerColor.text};
  }
`;

const ChangeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.hitTarget}px;
  color: ${careerColor.text2};
  text-decoration: underline;
  text-underline-offset: 3px;
  ${careerType.body()};

  &:hover {
    color: ${careerColor.text};
  }
`;
